import React, { Component } from "react";
import sortablejs, { ItemInterface } from "react-sortablejs";
import UploadPreviewFile from "../../ui/upload-preview";
import { fetchJson } from "../../utils";
import MarketplaceUploadForm, { MarketplaceUploadFormData } from "./form";
import HttpError from "../../utils/HttpError";
import { UploadConfig } from "./config-types";
const { ReactSortable } = sortablejs;
import "./style.scss";

interface UploadImageItemInterface {
    id: number;
    image: string;
    position: number;
}

type UploadImageType = {
    file?: File;
    progress?: number;
    error?: string;
    preview?: string;
} & Partial<UploadImageItemInterface> & ItemInterface;

type UploadImageResponse = {
    data: UploadImageItemInterface[];
    success: boolean;
};

interface MarketplaceUploadState {
    isDragOver: boolean;
    caption: string;
    loading: boolean;
    submitting: boolean;
    submitted: boolean;
    formError: string | null;
    disableSortable: boolean;
}

interface MarketplaceUploadProps {
    config: UploadConfig;
}

class MarketplaceUpload extends Component<MarketplaceUploadProps, MarketplaceUploadState> {
    private uploadedFiles: UploadImageType[] = [];
    private readonly maxPhotos: number;
    private isTouchDevice: boolean = false;

    /**
     * Initializes the MarketplaceUpload component.
     * @param props - The component props
     */
    constructor(props: MarketplaceUploadProps) {
        super(props);

        this.state = {
            isDragOver: false,
            caption: '',
            loading: true,
            submitting: false,
            submitted: false,
            formError: null,
            disableSortable: false,
        };
        this.maxPhotos = props.config.maxPhotos || 200;
        this.isTouchDevice = 'ontouchstart' in window || window.navigator.maxTouchPoints > 0;
    }

    /**
     * Loads previously uploaded files from the API.
     * Updates the component state with the loaded files.
     * @returns Promise<void>
     */
    private apiLoadUploadedFiles = async (): Promise<void> => {
        try {
            const { json } = await fetchJson<UploadImageResponse>(this.props.config.endpoints.load, {
                method: 'GET',
            });
            const { data, success } = json;
            if (!success) {
                return;
            }
            this.uploadedFiles = data.map((v, index) => ({
                ...v,
                position: index,
                preview: `/api/media/${v.image}?s=s`,
            }));

    
        } catch (error) {
            console.error('Error loading uploaded files:', error);
        } finally {
            this.setState({
                loading: false,
            });
        }
    };

    /**
     * Removes an uploaded file from the server.
     * @param id - The ID of the file to remove
     * @returns Promise<void>
     */
    private apiRemoveUploadedFile = async (id: number): Promise<void> => {
        try {
            this.setState({ disableSortable: true });
            const { json } = await fetchJson<UploadImageResponse>(`${this.props.config.endpoints.remove}/${id}`, {
                method: 'DELETE',
            });
            const { data, success } = json;
            if (!success) {
                return;
            }
            this.uploadedFiles = data.map((v) => ({
                ...v,
                preview: `/api/media/${v.image}?s=s`
            }));
            this.updatePositions(this.uploadedFiles);
            this.forceUpdate();
        } catch (error) {
            console.error('Error removing uploaded file:', error);
        } finally {
            this.setState({ disableSortable: false });
        }
    };

    /**
     * Updates the positions of uploaded files on the server.
     * @returns Promise<void>
     */
    private apiUpdatePositions = async (): Promise<void> => {
        
        try {
            this.setState({ disableSortable: true });
            const { json } = await fetchJson<UploadImageResponse>(this.props.config.endpoints.updatePositions, {
                method: 'POST',
                body: JSON.stringify(this.uploadedFiles.map((v, index) => ({ id: v.id, position: index }))),
            });
            const { data, success } = json;
            if (!success) {
                return;
            }
            this.uploadedFiles = data.map((v, index) => ({
                ...v,
                position: index,
                preview: `/api/media/${v.image}?s=s`,
            }));
        } catch (error) {
            console.error('Error updating positions:', error);
        } finally {
            this.setState({ disableSortable: false });
        }
    };

    /**
     * React lifecycle method called after component mounting.
     * Loads the initial uploaded files.
     */
    componentDidMount(): void {
        this.apiLoadUploadedFiles();
    }

  
    /**
     * Generates a unique upload ID for a file.
     * @param file - The file to generate an ID for
     * @returns string - The generated upload ID
     */
    private generateUploadId(file: File): string {
       // Encode the filename to handle non-Latin characters
       const encodedName = encodeURIComponent(file.name);
       // Add timestamp for uniqueness
       const timestamp = Date.now();
       // Create a hash-like string that's safe for HTTP headers
       return `file-${timestamp}-${encodedName.replace(/%/g, '')}`;
    }

    /**
     * Handles file upload when files are selected.
     * @param files - The FileList containing selected files
     */
    private handleFileUpload = (files: FileList | null): void => {
        if (!files) return;

        const newFiles = Array.from(files)
            .map((file, index) => ({
                file,
                position: index + this.uploadedFiles.length
            }))
            .slice(0, Math.max(this.maxPhotos - this.uploadedFiles.length, 0)) as UploadImageType[];

        this.uploadedFiles = [...this.uploadedFiles, ...newFiles];
        this.processUploadedFiles();
    };

    /**
     * Processes uploaded files by initiating file processing and upload.
     */
    private processUploadedFiles = (): void => {
        this.uploadedFiles.forEach((props, index) => {
            const { file, progress } = props;
            if (file && progress === undefined) {
                this.handleFileProcessing(file, index);
            } else if (progress === 100 && file) {
                this.uploadFile(file, index);
            }
        });
    };

    /**
     * Processes a file by creating a preview and preparing it for upload.
     * @param file - The file to process
     * @param index - The index of the file in the uploadedFiles array
     */
    private handleFileProcessing = (file: File, index: number): void => {
        const img = new Image();
        const objectURL = URL.createObjectURL(file);

        img.onload = () => {
            const { width, height } = img;

            if (width > 300) {
                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d");
                if (ctx) {
                    const scale = 300 / width;
                    canvas.width = 300;
                    canvas.height = height * scale;
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                    const resizedDataUrl = canvas.toDataURL("image/jpeg");
                    this.updateUploadedFile(index, { preview: resizedDataUrl, progress: 2 });
                }
            } else {
                this.updateUploadedFile(index, { preview: objectURL, progress: 2 });
            }

            URL.revokeObjectURL(objectURL);
            this.uploadFile(file, index);
        };

        img.onerror = () => {
            this.updateUploadedFile(index, { error: "Error loading image", file: undefined, progress: undefined });
        };

        img.src = objectURL;
    };

    /**
     * Uploads a file on the server using XHR for progress tracking.
     * @param options - The upload options including file, position, headers, and callbacks
     * @returns Promise<void>
     */
    private apiUploadeFile = async ({
        image,
        position,
        headers,
        onProgress,
        onError,
    }: {
        position: number;
        image: File | string;
        headers?: HeadersInit;
        onProgress?: (progress?: number) => void;
        onError?: (error: Error) => void;
    }): Promise<void> => {
        const formData = new FormData();
        formData.append("image", image);
        formData.append("position", String(position));

        const xhr = new XMLHttpRequest();

        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable && onProgress) {
                const progress = (event.loaded / event.total) * 100;
                onProgress(progress);
            }
        };

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    const { data } = JSON.parse(xhr.responseText) as UploadImageResponse;
                    
                    // Find the uploaded file in the response
                    const uploadedFile = data.find(v => v.position === position);
                    if (uploadedFile) {
                        // Update only this specific file in the array
                        this.uploadedFiles = this.uploadedFiles.map(uploadInfo => 
                            uploadInfo.position === position 
                                ? {
                                    ...uploadInfo,
                                    ...uploadedFile,
                                    preview: `/api/media/${uploadedFile.image}?s=s`,
                                    file: undefined,
                                    progress: undefined,
                                    error: undefined,
                                }
                                : uploadInfo
                        );
                        this.forceUpdate();
                    }
                } catch (error) {
                    const err = new Error('Invalid JSON response');
                    console.error("Failed to update image:", err);
                    if (onError) onError(err);
                }
            } else {
                const err = new Error(`HTTP error! status: ${xhr.status}`);
                console.error("Failed to update image:", err);
                if (onError) onError(err);
            }
        };

        xhr.onerror = () => {
            const err = new Error('Network request failed');
            console.error("Failed to update image:", err);
            if (onError) onError(err);
        };

        xhr.open('POST', this.props.config.endpoints.upload, true);
        xhr.setRequestHeader('Accept', 'application/json');

        if (headers) {
            if (headers instanceof Headers) {
                headers.forEach((value, key) => {
                    xhr.setRequestHeader(key, value);
                });
            } else {
                Object.entries(headers).forEach(([key, value]) => {
                    if (typeof value === 'string') {
                        xhr.setRequestHeader(key, value);
                    }
                });
            }
        }

        xhr.send(formData);
    };

    /**
     * Uploads a file, handling both regular and chunked uploads.
     * @param file - The file to upload
     * @param index - The index of the file in the uploadedFiles array
     * @returns Promise<void>
     */
    private uploadFile = async (file: File, index: number): Promise<void> => {
        if (file?.size > 2 * 1024 * 1024) {
            const uploadId = this.generateUploadId(file);
            try {
                await this.uploadFileInChunks(file, uploadId, (progress) => {
                    this.updateUploadedFile(index, { progress });
                });

                await this.apiUploadeFile({
                    position: index,
                    image: `{{${uploadId}}}`,
                    headers: new Headers({
                        'X-Chunk-ID': uploadId,
                        'x-chunk-name': encodeURIComponent(file.name),
                    }),
                    onError: (error) => {
                        console.error('Error occurred during upload:', error);
                        this.updateUploadedFile(index, { error: "Error while uploading", file: undefined, progress: undefined });
                    },
                    onProgress: (progress) => {
                        if (progress !== undefined) {
                            this.updateUploadedFile(index, { progress });
                        }
                    }
                });
            } catch (error) {
                console.error('Error occurred during upload:', error);
                this.updateUploadedFile(index, { error: "Error while uploading", file: undefined, progress: undefined });
            }
        } else {
            await this.apiUploadeFile({
                position: index,
                image: file,
                onError: (error) => {
                    console.error('Error occurred during upload:', error);
                    this.updateUploadedFile(index, { error: "Error while uploading", file: undefined, progress: undefined });
                },
                onProgress: (progress) => {
                    if (progress !== undefined) {
                        this.updateUploadedFile(index, { progress });
                    }
                }
            });
        }
    };

    /**
     * Uploads a file in chunks for large files.
     * @param file - The file to upload
     * @param uploadId - The unique upload ID
     * @param onProgress - Callback for upload progress
     * @returns Promise<void>
     */
    private async uploadFileInChunks(file: File, uploadId: string, onProgress?: (progress: number) => void): Promise<void> {
        const CHUNK_SIZE = 2 * 1024 * 1024; // 2MB chunks
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
        const fileName = file.name;
        let totalProgress = 0;

        for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
            const start = chunkIndex * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE, file.size);
            const chunkBlob = file.slice(start, end);
            const baseProgress = (chunkIndex / totalChunks) * 100;

            await new Promise<void>((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                let attempts = 0;
                const MAX_RETRIES = 3;

                if (attempts > 0 && onProgress) {
                    onProgress(baseProgress);
                }

                const attemptUpload = () => {
                    xhr.open('POST', '/api/media/upload', true);
                    xhr.setRequestHeader('X-Chunk-Index', String(chunkIndex));
                    xhr.setRequestHeader('x-chunk-length', String(totalChunks));
                    xhr.setRequestHeader('X-Chunk-Name', encodeURIComponent(fileName));
                    xhr.setRequestHeader('X-Chunk-ID', uploadId);
                    xhr.setRequestHeader('X-Chunk-Type', file.type);

                    xhr.upload.onprogress = (e) => {
                        if (e.lengthComputable && onProgress) {
                            const chunkProgress = (e.loaded / e.total) * (100 / totalChunks);
                            totalProgress = baseProgress + chunkProgress;
                            onProgress(Math.min(totalProgress, 100));
                        }
                    };

                    xhr.onload = () => {
                        if (xhr.status >= 200 && xhr.status < 300) {
                            const chunkStatus = xhr.getResponseHeader('X-Chunk-Status');
                            if (chunkStatus === 'complete') {
                                if (onProgress) onProgress(100);
                                resolve();
                                return;
                            }
                            resolve();
                        } else {
                            if (attempts < MAX_RETRIES) {
                                attempts++;
                                attemptUpload();
                            } else {
                                reject(new Error(`Failed to upload chunk ${chunkIndex + 1} after ${MAX_RETRIES} attempts.`));
                            }
                        }
                    };

                    xhr.onerror = () => {
                        if (attempts < MAX_RETRIES) {
                            attempts++;
                            attemptUpload();
                        } else {
                            reject(new Error(`Network error uploading chunk ${chunkIndex + 1} after ${MAX_RETRIES} attempts.`));
                        }
                    };

                    xhr.send(chunkBlob);
                };

                attemptUpload();
            });
        }
    }

    /**
     * Updates a file in the uploadedFiles array.
     * @param index - The index of the file to update
     * @param props - The properties to update
     */
    private updateUploadedFile = (index: number, props: Partial<UploadImageType>): void => {
        this.uploadedFiles = this.uploadedFiles.map((file, i) => (i === index ? { ...file, ...props } : file));
        this.forceUpdate();
    };

    /**
     * Updates the positions of files and triggers server update if needed.
     * @param updatedList - The updated list of files
     */
    private updatePositions = (updatedList: UploadImageType[]): void => {
        // Update the list immediately for smooth drag and drop
        this.uploadedFiles = updatedList.map((v, i) => ({ ...v, position: i }));
        this.forceUpdate();

        // Check if we need to sync with server
        const needToUpdate = updatedList.reduce((acc, { position }, i) => {
            if (acc) return true;
            return position !== i;
        }, false);

        if (needToUpdate) {
            this.apiUpdatePositions();
        }
    };

    /**
     * Removes a file from the upload list.
     * @param file - The file to remove
     */
    private removeFile = (file: UploadImageType): void => {
        if (file.id) {
            this.apiRemoveUploadedFile(file.id);
        } else if (file.position !== undefined) {
            this.uploadedFiles = this.uploadedFiles.filter((v) => v.position !== file.position);
            this.uploadedFiles = this.uploadedFiles.map((v, i) => ({ ...v, position: i }));
            this.forceUpdate();
        }
    };

    /**
     * Handles file drop events.
     * @param e - The drag event
     */
    private handleDrop = (e: React.DragEvent): void => {
        e.preventDefault();
        const files = e.dataTransfer.files;
        if (files && files.length) {
            this.handleFileUpload(files);
        }
        this.setState({ isDragOver: false });
    };

    /**
     * Handles drag over events.
     * @param e - The drag event
     */
    private handleDragOver = (e: React.DragEvent): void => {
        e.preventDefault();
    };

    /**
     * Handles drag enter events.
     * @param e - The drag event
     */
    private handleDragEnter = (e: React.DragEvent): void => {
        e.preventDefault();
        this.setState({ isDragOver: true });
    };

    /**
     * Handles drag leave events.
     * @param e - The drag event
     */
    private handleDragLeave = (e: React.DragEvent): void => {
        e.preventDefault();
        this.setState({ isDragOver: false });
    };

    /**
     * Updates form values on the server.
     * @param data - The form data to update
     * @returns Promise<void>
     */
    private updateFormValues = async (data: MarketplaceUploadFormData): Promise<void> => {
        try {
            this.setState({ submitting: true, formError: null });
            const { json } = await fetchJson<UploadImageResponse>(this.props.config.endpoints.submit, {
                method: 'POST',
                body: JSON.stringify({
                    ...data,
                    images: this.uploadedFiles.map((v) => v.id),
                }),
            });
            if (json.success) {
                console.log('Form submitted successfully:', json);
                this.uploadedFiles = [];
                this.setState({ submitted: true });
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            if (error instanceof HttpError) {
                this.setState({ formError: error.body.error });
            }
        } finally {
            this.setState({ submitting: false });
        }
    };

    /**
     * Converts a hex color to RGB format.
     * @param hex - The hex color string
     * @returns string - The RGB color string
     */
    private hexToRgb(hex: string): string {
        hex = hex.replace(/^#/, '');
        const bigint = parseInt(hex, 16);
        const r = (bigint >> 16) & 255;
        const g = (bigint >> 8) & 255;
        const b = bigint & 255;
        return `${r}, ${g}, ${b}`;
    }

    /**
     * Renders the component.
     * @returns React.ReactNode
     */
    render(): React.ReactNode {
        const { isDragOver, disableSortable } = this.state;
        const { config } = this.props;
        const selectedCount = this.uploadedFiles.filter((v) => !!v.id).length;
        const dropzoneHighlightStyle = isDragOver ? {
            backgroundColor: `rgba(${this.hexToRgb(getComputedStyle(document.documentElement).getPropertyValue('--primary'))}, 0.1)`,
            borderColor: `var(--primary)`,
        } : {};

        const uploading = this.uploadedFiles.some(file =>
            file.progress !== undefined &&
            file.progress < 100 &&
            !file.error
        );

        if (this.state.submitted) {
            return <div className="inside-wrapper upload__marketplace">
                <h1 className="section__title">{config.successMessage || "Your photos have been successfully uploaded!"}</h1>
                <p className="upload__marketplace-success-text">
                    Expect delivery in the near future.
                </p>
            </div>
        }

        const isTouchDevice = this.isTouchDevice;
        return (
            <div className="upload upload__marketplace">
                <div className="upload__inner inside-wrapper">
                    <div className="upload__center">
                        <div className="upload__body">
                            <div className="upload__content dropzone">
                                <div className="upload__content-title dropzone__title">
                                    {config.title || "Drag & Drop"} <br /> any files
                                </div>
                                <div className="upload__content-text dropzone__text">or</div>
                                <div className="upload__content-btns">
                                    <div className="dropzone__holder">
                                        <button className="btn btn_alt btn_lg" type="button">
                                            UPLOAD FROM CAMERA ROLL OR PC
                                        </button>
                                        <div className="dropzone__box"
                                            style={dropzoneHighlightStyle}
                                            onDrop={this.handleDrop}
                                            onDragOver={this.handleDragOver}
                                            onDragEnter={this.handleDragEnter}
                                            onDragLeave={this.handleDragLeave}
                                        >
                                            <input
                                                type="file"
                                                className="dropzone__input"
                                                multiple
                                                accept="image/*"
                                                onChange={(e) => this.handleFileUpload(e.target.files)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <MarketplaceUploadForm
                        className="upload__right"
                        onSubmit={this.updateFormValues}
                        uploadedFilesCount={selectedCount}
                        formError={this.state.formError}
                        fields={config.fields}
                        title={config.subtitle}
                    >
                        <div className="upload__content-btns">
                                    <div className="dropzone__holder">
                                        <button className="btn btn_alt btn_lg" type="button">
                                            UPLOAD FROM CAMERA ROLL OR PC
                                        </button>
                                        <div className="dropzone__box"
                                            style={dropzoneHighlightStyle}
                                            onDrop={this.handleDrop}
                                            onDragOver={this.handleDragOver}
                                            onDragEnter={this.handleDragEnter}
                                            onDragLeave={this.handleDragLeave}
                                        >
                                            <input
                                                type="file"
                                                className="dropzone__input"
                                                accept="image/*"
                                                multiple
                                                onChange={(e) => this.handleFileUpload(e.target.files)}
                                            />
                                        </div>
                                    </div>
                                </div>
                        <div className="upload__preview">
                            <div className="upload__preview-head">
                                <div className="upload__preview-title">Selection preview</div>
                                <p>
                                    Drag and drop the photos to arrange them in the order you want to
                                    see them in the video.
                                </p>
                            </div>
                            <div className="upload__preview-list-holder">
                                <ReactSortable
                                    className="upload__preview-list js_sortable"
                                    handle={isTouchDevice ? ".upfile" : ".js_sortable_handle"}
                                    animation={250}
                                    list={this.uploadedFiles}
                                    setList={this.updatePositions}
                                    scroll={true}
                                    scrollSensitivity={30}
                                    scrollSpeed={10}
                                    direction="vertical"
                                    delay={50}
                                    delayOnTouchOnly={true}
                                    touchStartThreshold={3}
                                    forceFallback={true}
                                    disabled={disableSortable || uploading}
                                >
                                    {this.uploadedFiles.map((uploadable, index) => (
                                        <UploadPreviewFile
                                            key={index}
                                            index={index}
                                            removeFile={this.removeFile}
                                            hideMoveButton={uploading}
                                            disableRemoveButton={uploading || disableSortable}
                                            {...uploadable}
                                        />
                                    ))}
                                </ReactSortable>
                            </div>
                        </div>
                        <div className="upload__bottom">
                            <div className="upload__selected">
                                <div className="upload__selected-text">
                                    Upload as many photos as specified in the product description. Any photos uploaded beyond the limit will not be accepted for the video story production.
                                </div>
                                <div className="upload__selected-text">
                                    Selected {selectedCount} {selectedCount === 1 ? 'picture' : 'pictures'}
                                </div>
                                <div className="upload__selected-btns">
                                    <button
                                        className="btn btn_cta"
                                        disabled={uploading || this.state.submitting}
                                        type="submit"
                                    >
                                        {uploading ? 'Waiting while uploading...' : 'Send'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </MarketplaceUploadForm>
                </div>
            </div>
        );
    }
}

export default MarketplaceUpload;